import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { track } from "@/lib/track";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const REF_KEY = "c2g_ref";
const refParam = new URLSearchParams(window.location.search).get("ref");
if (refParam && /^[A-Za-z0-9]{4,20}$/.test(refParam)) localStorage.setItem(REF_KEY, refParam.toUpperCase());

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth

  const [offline, setOffline] = useState(false);

  const refreshUser = useCallback(async () => {
    let refreshed = false;
    for (let attempt = 0; ; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setOffline(false);
        setUser(data);
        return data;
      } catch (e) {
        const status = e.response?.status;
        if ((status === 401 || status === 403) && !refreshed) {
          refreshed = true;
          try { await api.post("/auth/refresh"); continue; } catch { /* not logged in */ }
        }
        if (status === 401 || status === 403) {
          setUser(false);
          return false;
        }
        if (attempt >= 5) {
          setUser((prev) => {
            if (!prev) setOffline(true);
            return prev || false;
          });
          return false;
        }
        await new Promise((r) => setTimeout(r, 2000 + attempt * 2000));
      }
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, phone) => {
    const { data } = await api.post("/auth/register", { email, password, name, phone, ref: localStorage.getItem(REF_KEY) || undefined });
    localStorage.removeItem(REF_KEY);
    track("register");
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refreshUser, offline }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
